import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { chartTheme } from '../utils/chartTheme';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { useTheme } from '../context/ThemeContext';

interface TrendTotalProps {
  kommunId: string;
  skolaId: string | null;
  kpiId: string;
  selectedYear: number;
  yDomain?: [number, number];
  yearTicks?: number[];
  compareScope?: 'municipality' | 'national';
}

// (moved into component to access compareScope)

const TrendTotal: React.FC<TrendTotalProps> = ({
  kommunId,
  skolaId,
  kpiId,
  selectedYear,
  yDomain,
  yearTicks,
  compareScope = 'municipality'
}) => {
  const { query } = useDuckDB();
  const { theme: themeMode } = useTheme();
  const [totalData, setTotalData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = chartTheme(themeMode);
  const colors = useMemo(
    () => ({
      school: theme.series.school || '#047857',
      municipality: theme.series.reference || '#3f3f46',
    }),
    [theme.series.school, theme.series.reference]
  );
  const themeKey = useMemo(
    () => [themeMode, theme.axis, colors.school, colors.municipality, theme.grid].join('|'),
    [themeMode, theme.axis, colors.school, colors.municipality, theme.grid]
  );
  // Fetch chart data (from original KpiCardRecharts)
  const fetchChartData = useCallback(async () => {
    const sql = `
      WITH school_data AS (
        SELECT year, value AS school_value
        FROM gold_kpi_trend
        WHERE entity_type='org_unit' AND entity_id='${skolaId}' AND kpi_id='${kpiId}'
          AND gender = 'T' AND year <= ${selectedYear}
      ),
      ref_data AS (
        ${compareScope === 'municipality'
          ? `SELECT year, value_t AS ref_value
             FROM gold_kpi_muni_year
             WHERE municipality_id='${kommunId}' AND kpi_id='${kpiId}' AND year <= ${selectedYear}`
          : `SELECT year, AVG(value) AS ref_value
             FROM gold_kpi_trend
             WHERE entity_type='org_unit' AND kpi_id='${kpiId}' AND gender='T' AND year <= ${selectedYear}
             GROUP BY year`}
      ),
      ref_band AS (
        ${compareScope === 'municipality'
          ? `SELECT year, MIN(value) AS ref_min, MAX(value) AS ref_max
             FROM gold_kpi_trend
             WHERE entity_type='org_unit' AND kpi_id='${kpiId}' AND gender='T' AND municipality_id='${kommunId}' AND year <= ${selectedYear}
             GROUP BY year`
          : `SELECT year, MIN(value) AS ref_min, MAX(value) AS ref_max
             FROM gold_kpi_trend
             WHERE entity_type='org_unit' AND kpi_id='${kpiId}' AND gender='T' AND year <= ${selectedYear}
             GROUP BY year`}
      )
      SELECT y.year, sd.school_value, rd.ref_value as muni_value, rb.ref_min as muni_min, rb.ref_max as muni_max
      FROM (SELECT year FROM school_data UNION SELECT year FROM ref_data UNION SELECT year FROM ref_band) y
      LEFT JOIN school_data sd ON sd.year = y.year
      LEFT JOIN ref_data rd ON rd.year = y.year
      LEFT JOIN ref_band rb ON rb.year = y.year
      ORDER BY y.year;
    `;
    
    try {
      const data = await query(sql);
      setTotalData(data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setTotalData([]);
    }
  }, [query, skolaId, kommunId, kpiId, selectedYear, compareScope]);

  // Load data
  useEffect(() => {
    if (kommunId && skolaId && kpiId) {
      setLoading(true);
      setTotalData([]);
      fetchChartData().finally(() => {
        setLoading(false);
      });
    }
  // We intentionally omit fetchChartData to avoid re-creating the function and looping
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kommunId, skolaId, kpiId, selectedYear, compareScope]);

  if (loading) {
    return (
      <div className="h-44 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Laddar trend...</div>
      </div>
    );
  }

  return (
    <div className="h-52">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart key={themeKey} data={totalData} margin={{ top: 8, right: 35, left: 25, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
          <XAxis 
            dataKey="year" 
            type="category"
            allowDuplicatedCategory={false}
            tick={{ fontSize: 12, fill: theme.axis }}
            tickLine={{ stroke: theme.axis }}
            axisLine={{ stroke: theme.axis }}
            ticks={yearTicks as any}
          />
          <YAxis 
            domain={yDomain || ['dataMin - 10', 'dataMax + 10']}
            tick={{ fontSize: 12, fill: theme.axis }}
            tickLine={{ stroke: theme.axis }}
            axisLine={{ stroke: theme.axis }}
            tickCount={6}
            allowDecimals={false}
            tickFormatter={(v: number) => `${Math.round(Number(v))}`}
          />
          <Tooltip 
            trigger="click"
            contentStyle={{ background: theme.tooltipBg, color: theme.tooltipFg, border: `1px solid ${theme.grid}` }}
            formatter={(val: number, name: string) => {
              const label = name === 'Vald enhet' ? 'Skola' : (compareScope === 'municipality' ? 'Kommun' : 'Riket');
              const v = Number(val);
              return [`${Number.isFinite(v) ? Math.round(v) : v}` , label];
            }}
          />
          <Line 
            type="monotone" 
            dataKey="school_value" 
            stroke={colors.school} 
            strokeWidth={2}
            dot={{ fill: colors.school, strokeWidth: 2, r: 4 }}
            name="Vald enhet"
          />
          <Line 
            type="monotone" 
            dataKey="muni_value" 
            stroke={colors.municipality} 
            strokeWidth={2}
            dot={{ fill: colors.municipality, strokeWidth: 2, r: 4 }}
            name={compareScope === 'municipality' ? 'Kommun' : 'Riket'}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendTotal;
